import { faComment, faEnvelope } from '@fortawesome/free-regular-svg-icons'
import { faClipboard } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DiaperBank } from '@prisma/client'
import classNames from 'classnames'
import React, { useContext, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import Context from '../../utils/Context'
import { DiaperBankPublic } from '../../utils/DiaperBankDTO'
import { gaEvent } from '../../utils/GA'
import Button from '../form/Button'
import FormFieldInput from '../form/FormFieldInput'
import { toastMessage } from '../toastMessage'

interface EmailShareForm {
  name: string
  recipients: string
  message?: string
}

export default function ShareContent({ diaperBank }: { diaperBank: DiaperBankPublic }) {
  const [hasCopied, setHasCopied] = useState<boolean>(false)
  const [showEmailShare, setShowEmailShare] = useState<boolean>(false)
  const context = useContext(Context)

  const userIsAdminOfThisDiaperBank = context.role === 'admin' && context.jwtToken?.ownedDiaperBankSlugs?.includes(diaperBank.slug)

  const {
    handleSubmit,
    formState: { errors: formErrors },
    register,
  } = useForm<EmailShareForm>({ defaultValues: { name: userIsAdminOfThisDiaperBank ? `${diaperBank.recipientFirstName} ${diaperBank.recipientLastName}` : `` } })

  const { mutateAsync, isLoading } = useMutation((share: EmailShareForm) => {
    return context.apiClient.post<DiaperBank>(`diaperBanks/${diaperBank.slug}/email-share`, { share })
  })

  const copied = () => {
    setHasCopied(true)
    const timer = setTimeout(() => setHasCopied(false), 5000)
  }

  const sendEmails = async (data: EmailShareForm) => {
    try {
      await mutateAsync(data)
      toastMessage({ title: 'Email has been sent!', type: 'success' })
      setShowEmailShare(false)
      recordEvent('email')
    } catch (error) {
      console.error(error)
      toastMessage({ title: 'Something went wrong', type: 'error' })
    }
  }

  const recordEvent = (type: string) => {
    gaEvent({
      action: `df_share`,
      params: {
        event_label: type,
      },
    })
  }

  return (
    <div>
      <div className='bg-white border border-gray-300 rounded-md'>
        <ul role='list' className='divide-y divide-gray-300'>
          <li className='px-4 py-4 flex'>
            <div className='mr-2'>
              <FontAwesomeIcon icon={faClipboard} className={classNames({ 'text-success': hasCopied })}></FontAwesomeIcon>
            </div>
            <div className='flex-grow'>
              <CopyToClipboard
                text={diaperBank.shareUrl}
                onCopy={() => {
                  copied()
                  recordEvent('copy_link')
                }}
              >
                <button className='inline-flex items-center focus:outline-none'>Copy Diaper Fund Link</button>
              </CopyToClipboard>

              {hasCopied && <span className='text-success'>&nbsp;&nbsp;Copied!</span>}
            </div>
          </li>
          <li className='px-4 py-4 flex'>
            <div className='mr-2'>
              <FontAwesomeIcon icon={faComment}></FontAwesomeIcon>
            </div>
            <div className='flex-grow'>
              <a href={`sms:?&body=Check out ${diaperBank.babyName}'s Diaper Fund. ${diaperBank.shareUrl}`} onClick={() => recordEvent('text')}>
                Text Message
              </a>
            </div>
          </li>

          {userIsAdminOfThisDiaperBank && (
            <li className='px-4 py-4 flex'>
              <div className='mr-2'>
                <FontAwesomeIcon icon={faEnvelope}></FontAwesomeIcon>
              </div>
              <div className='flex-grow'>
                <button onClick={() => setShowEmailShare(!showEmailShare)}>Email</button>
              </div>
            </li>
          )}
        </ul>
        <div className={classNames('transition-all ', { 'opacity-0 max-h-0 h-0': !showEmailShare, 'px-4  opacity-100 max-h-80': showEmailShare })}>
          <form onSubmit={handleSubmit(sendEmails)} className={classNames({ 'mb-6 px-4': showEmailShare, hidden: !showEmailShare })}>
            <FormFieldInput register={register('name', { required: true })} label='Your Name' type='text' errors={formErrors} />
            <FormFieldInput
              register={register('recipients', {
                required: true,
                pattern: {
                  value: /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*,{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/,
                  message: 'Enter valid email(s) separated by comma',
                },
              })}
              label='Recipients'
              helpText={'Enter comma separated email addresses'}
              type='text'
              errors={formErrors}
            />
            <FormFieldInput register={register('message')} label='Message' helpText={'Include a personalized message'} type='textarea' errors={formErrors} />

            <Button type='submit' color='primary' fullWidth loading={isLoading}>
              Send Emails
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
